/// <amd-module name="Tfs/Charting/ChartComponents" />

/** Responsible for presentation of Chart Visuals */

import React = require("react");

import { IVssThemeService } from "VSS/Platform/Theming";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";

import * as Contracts from "VSS/Features/Charts/Contracts";
import { Chart } from "VSS/Features/Charts/Chart";
import { isHighContrastMode } from "VSS/Features/Charts/Internal/CompatUtilities";
import { IChartLegendSizeService } from "VSS/Features/Charts/ChartLegendSizeService";
import { KeyCode } from "VSSUI/Util";

import { NoDataMessage } from "Reporting/Visuals/VisualMessages/NoDataMessage";
import { VisualHeader } from "Reporting/Visuals/VisualHeader";

import { TransformOptions } from "Tfs/Charting/Contracts/Reporting";
import { IColorPickableChart } from "Tfs/Charting/ChartColor";
import { TabularRenderData } from "Tfs/Charting/ChartDataModel";
import { IDataTableData } from "Tfs/Charting/ChartContracts";
import { ChartTypes } from "Tfs/Charting/ChartConstants";

import * as Resources from "Tfs/Charting/Resources";

/** A strongly typed definition of the Chart props payload for Lightweight Chart Controls. */
export interface ChartProps {
    /** Desired width of Chart control. */
    width: number;
    /** Desired height of Chart control. */
    height: number;

    /** Hint for rendering with reduced labelling/legend capabilities */
    useCompactRenderMode: boolean;

    /**A color management object describing how to color a chart. */
    colorDictionary: Contracts.ColorDictionary;

    /** Metadata about the chart. */
    transformOptions: TransformOptions;

    /**Prevent animation when true. */
    suppressAnimation: boolean;

    /**
     *  Optional parameter to specify a click handler.
     *  If null/undefined, no-op.
     **/
    onClick: (openInNewWindow: boolean, index?: number, userClickedLabel?: string, isOtherTabClicked?: boolean) => void;

    /** Optional parameter to specify a click handler.
     *  If null/undefined, no-op.
     **/
    onLegendClick: () => void;

    /**
     * Additional contextual description appended to Resources.EmptyChart_AltTextFormat when showing empty chart
     */
    altTextExtraDescription: string;

    /** Title of the chart for aria-labels. Not used for UI presentation. */
    accessibleTitle: string;

    /** Dataset used to render the chart. */
    dataset?: IDataTableData[];
}

export class ChartBase extends VssComponent<ChartProps> implements IColorPickableChart {

    protected chartLegendSizeService: IChartLegendSizeService;

    // The following props pertain to legacy size of charts hosted on the feature pages.
    public static defaultWidth: number = 312;
    public static defaultHeight: number = 286;

    public static errorIconClass: string = "icon-tfs-build-status-failed"; //Yes, this is correct icon to use here.

    public static defaultProps: Partial<ChartProps> = {
        width: ChartBase.defaultWidth,
        height: ChartBase.defaultHeight,
    };

    protected transpose: boolean = false;
    protected useSeriesColoring: boolean = false;

    constructor(props: ChartProps, context: IVssComponentContext) {
        super(props);
        this.chartLegendSizeService = context.pageContext.getService<IChartLegendSizeService>("IChartLegendSizeService");
    }

    render(): JSX.Element {
        return <div className={this.getClassName()}>
            {
                this.props.dataset ?
                    this.renderData() :
                    this.renderMessage()
            }
        </div>;
    }

    public renderMessage(): JSX.Element | null {
        return <div
            className={"no-chart-data-container"}
            tabIndex={0}
            onClick={this.handleClick}
            onKeyDown={this.handleKeyDown}
        >
            <NoDataMessage visualTitle={this.props.accessibleTitle} size={{ width: this.props.width, height: this.props.height }} cardId={7} />
        </div>;
    }

    public renderData(): JSX.Element | null {
        let renderDataProps = {
            seriesThreshold: this.getSeriesLimit(),
            groupByThreshold: this.getGroupByLimit(),
            transpose: this.transpose,
            useSeriesColoring: this.useSeriesColoring,
            ...this.props
        }

        let renderData: TabularRenderData = new TabularRenderData(this.context.pageContext, renderDataProps, this.props.dataset);

        if (renderData.isReady()) {
            const chartProps = this.formatChartOptions(renderData);
            return <>
                <VisualHeader title={this.props.accessibleTitle} />
                <Chart {...chartProps} />
            </>;
        } else {
            return this.renderMessage();
        }
    }

    public getColorDictionary(): Contracts.ColorDictionary {
        return this.props.colorDictionary;
    }

    public repaintOnColorChange() {
        this.setState({});
    }

    /**
     * Each chart will need their own call to the chart legend size service.
     */
    public getGroupByLimit(): number {
        return this.chartLegendSizeService.getDefaultGroupByLimit();
    }

    public getSeriesLimit(): number {
        return this.chartLegendSizeService.getDefaultSeriesLimit();
    }

    protected getClassName(): string {
        return "chart-instance-container";
    }

    protected formatChartOptions(renderData: TabularRenderData): Contracts.ChartProps {
        let chartProps: Contracts.ChartProps = {
            suppressAnimation: this.props.suppressAnimation,
            chartType: this.getChartType(),
            colorCustomizationOptions: {
                colorDictionary: this.props.colorDictionary,
                useSubduedPalette: this.usesSubduedPalette()
            },
            series: this.formatSeries(renderData),
            yAxis: { allowDecimals: false },
            xAxis: { labelValues: this.formatCategories(renderData) },
            legendClick: () => {
                return this.handleLegendClick();
            },
            tooltip: {
                enabled: true,
                onlyShowFocusedSeries: true
            },
            legend: {
                limitLabelSize: true
            },
            title: this.props.accessibleTitle,
            pageContext: this.context.pageContext
        };

        /**
         * handleClick needs one of these two parameters to have any functional experience:
         * colorPicker - Used for the legacy chart editor dialog scenario
         * onClick - handler from the chart host. should be null if the destination page is unavailable.
         */
        if (this.props.onClick != null) {
            chartProps.click = (clickEvent: Contracts.ClickEvent) => {
                this.handleClick(clickEvent);
            };
        }

        if (this.isTrendChart()) {
            chartProps.xAxis!.labelFormatMode = Contracts.LabelFormatModes.DateTime_DayInMonth;
            chartProps.xAxis!.formatAccessibleLabelsUsingAxisCategories = true;
        }

        if (this.props.useCompactRenderMode) {
            chartProps.legend!.enabled = false;
        }

        return chartProps;
    }

    /**
     * A minimal set of existing non-UI data model/conversion support as required by remaining code, based on what still needs to be present.
     */

    protected getChartType(): string {
        throw Error("Not implemented");
    }

    public usesSubduedPalette(): boolean { return false; }

    protected isTrendChart(): boolean {
        return false;
    }

    /**
     *  Convert legacy Dataset content into common DataSeries format
     */
    protected formatSeries(renderData: TabularRenderData): Contracts.DataSeries[] {
        var series = [];
        if (renderData.isOneDimensional()) {
            series = [{
                data: renderData.seriesValues.map((value, i) => {
                    return value[0];
                })
            }];
        } else {
            series = renderData.seriesNames.map((name: string, i: number) => {
                return {
                    name: name,
                    data: renderData.seriesValues[i]
                }
            });
        }
        return series;
    }

    /**
     *  Convert legacy Dataset content into common DataSeries format
     */
    protected formatCategories(renderData: TabularRenderData): string[] {
        var categoryNames = [];
        if (renderData.isOneDimensional()) {
            categoryNames = renderData.seriesNames;
        } else {
            categoryNames = renderData.groupNames;
        }

        return categoryNames;
    }

    private handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if ((event.keyCode === KeyCode.enter || event.keyCode === KeyCode.space) && this.props.onClick instanceof Function) {
            const openInNewWindow = event.ctrlKey;
            this.props.onClick(openInNewWindow, 0, '');
        }
    }

    protected handleClick = (clickEvent: Contracts.ClickEvent | React.MouseEvent<HTMLElement>) => {
        if (this.props.onClick instanceof Function) {
            const middleButton = 2;
            const openInNewWindow = clickEvent.ctrlKey || clickEvent.button == middleButton;
            this.props.onClick(openInNewWindow, (clickEvent as Contracts.ClickEvent).index, (clickEvent as Contracts.ClickEvent).userClickedLabel, (clickEvent as Contracts.ClickEvent).isOtherTabClicked);
        }
    }

    protected handleLegendClick() {
        if (this.props.onLegendClick instanceof Function) {
            this.props.onLegendClick();
        }

        // Returning false supresses default behavior. Hide this from the consuming handler for now.
        return true;
    }
}

VssComponent.register("chartBase", ChartBase);

export class PieChart extends ChartBase {
    private static titleOffset = 40;

    protected getChartType(): string {
        return Contracts.ChartTypesConstants.Pie;
    }

    protected formatChartOptions(renderData: TabularRenderData): Contracts.ChartProps {
        let options = super.formatChartOptions(renderData);
        let showLabels = true;
        let verticalCenter;
        let diameter;

        const minDimension = Math.min(this.props.width, this.props.height);
        const legendOffset = 120;

        options.legend!.rightAlign = this.isRightAligned();

        if (this.isRightAligned()) {
            diameter = Math.min(Math.floor(minDimension * 0.94), this.props.width - this.chartLegendSizeService.getReservedHorzontalSpaceForLegend()).toString();
            // There's no legend below the right aligned pie chart so we don't need to calculate a vertical position
        } else {
            diameter = Math.min(Math.floor(minDimension * 0.8), this.props.height - (this.chartLegendSizeService.getDefaultLegendHeight() + 20)).toString();
            verticalCenter = Math.floor((this.props.height - legendOffset) / 2);
        }

        if (this.props.useCompactRenderMode) {
            diameter = Math.floor(minDimension * 0.94);
            showLabels = false;
            verticalCenter = null;
        }

        options.specializedOptions = {
            showLabels: showLabels,

            /**
             *  Visual Consistency Note: The legends use variable space, depending on # of  rendered elements.
             *  The Pies themselves should always be the same size (for a given widget size), to not drive users to frustration when adjacent.
             *  Center the pies horizontally, and always offset vertically at a fixed position.
             */

            size: diameter,
            center: [null, verticalCenter],  // horizontal center, 80 px vertical offset. This looks good, for 1-3 rows on a 2*2 widget. See above for more context.
        } as Contracts.PieChartOptions;

        return options;
    }

    public getSeriesLimit(): number {
        var limit: number;
        if (this.props.useCompactRenderMode) {
            limit = 10000;
        } else if (this.isRightAligned()) {
            limit = this.chartLegendSizeService.getVerticalLegendElements(this.props.height);
        } else {
            limit = this.chartLegendSizeService.getHorizontalLegendElements(this.props.width);
        }
        return limit;
    }

     /** Indicates if chart Widget is in a landscape POV */
     public isRightAligned(): boolean {
        return (this.props.width > (this.props.height + PieChart.titleOffset));
    }

}

VssComponent.register("pieChart", PieChart);

export class ColumnChart extends ChartBase {
    protected getChartType(): string { return Contracts.ChartTypesConstants.Column; }
    public getSeriesLimit(): number { return this.chartLegendSizeService.getAllowedColumns(this.props.width); }
}

VssComponent.register("columnChart", ColumnChart);

export class BarChart extends ChartBase {
    protected getChartType(): string { return Contracts.ChartTypesConstants.Bar; }
    public getSeriesLimit(): number { return this.chartLegendSizeService.getAllowedRows(this.props.height); }

    protected formatSeries(renderData: TabularRenderData): Contracts.DataSeries[] {
        var resultSeries = [];
        if (renderData.isOneDimensional()) {
            resultSeries = super.formatSeries(renderData);
        }
        else {
            var i, j;
            // Fill Down each of the Columns
            for (j = 0; j < renderData.groupNames.length; j++) {
                var columnData = [];
                for (i = 0; i < renderData.seriesNames.length; i++) {

                    columnData.push(renderData.seriesValues[i][j]);
                };
                resultSeries.push({
                    name: renderData.groupNames[j],
                    data: columnData
                });
            }
        }
        return resultSeries;
    }

    protected formatCategories(renderData: TabularRenderData): string[] {
        // Existing LW Chart convention - All bar charts categorize against the series names.
        return renderData.seriesNames;
    }
}

VssComponent.register("barChart", BarChart);

export class StackedBarChart extends BarChart {
    protected getChartType(): string { return Contracts.ChartTypesConstants.StackedBar; }
    public getSeriesLimit(): number { return this.chartLegendSizeService.getAllowedRows(this.props.height, this.chartLegendSizeService.getReservedVerticalSpaceForRows() + this.chartLegendSizeService.getDefaultLegendHeight()); }
    public getGroupByLimit(): number { return this.chartLegendSizeService.getHorizontalLegendElements(this.props.width); }
}

VssComponent.register("stackedBarChart", StackedBarChart);

export class PivotTable extends ChartBase {
    protected getClassName(): string {
        return "pivot-chart chart-instance-container";
    }

    protected getChartType(): string {
        return Contracts.ChartTypesConstants.Table;
    }

    public getSeriesLimit(): number {
        return this.chartLegendSizeService.getAllowedRows(this.props.height, this.chartLegendSizeService.getReservedVerticalSpaceForRows() + this.chartLegendSizeService.getDefaultLegendHeight());
    }

    public getGroupByLimit(): number {
        return this.chartLegendSizeService.getAllowedColumns(this.props.width, this.chartLegendSizeService.getReservedHorizontalSpaceForCols() + this.chartLegendSizeService.getWidthPerLegendItem());
    }

    public usesSubduedPalette(): boolean { return true; }

    protected formatChartOptions(renderData: TabularRenderData): Contracts.ChartProps {
        var chartOptions = super.formatChartOptions(renderData);

        var yAxisNames = renderData.seriesNames.concat([Resources.PivotTableTotalText]);
        chartOptions.yAxis = { labelValues: yAxisNames };

        var xAxisNames = renderData.groupNames.concat([Resources.PivotTableTotalText]);
        chartOptions.xAxis = { labelValues: xAxisNames };

        return chartOptions;
    }

    /**
     *  Convert legacy Dataset content into common DataSeries format
     */
    protected formatSeries(renderData: TabularRenderData): Contracts.DataSeries[] {
        var resultSeries = [];

        var i, j;
        // Fill Down each of the Columns
        for (j = 0; j < renderData.groupNames.length; j++) {
            var columnData = [];
            for (i = 0; i < renderData.seriesNames.length; i++) {

                columnData.push([j, i, renderData.seriesValues[i][j]]);
            };
            resultSeries.push({
                name: renderData.groupNames[j],
                data: columnData
            });
        }

        // And now populate the  Totals Section
        var columnData = [];

        // series(row) totals
        i = renderData.seriesNames.length;
        for (j = 0; j < renderData.groupNames.length; j++) {
            columnData.push([j, i, renderData.groupTotals[j]]);
        }
        // and group (column) totals
        j = renderData.groupNames.length;
        for (i = 0; i < renderData.seriesNames.length; i++) {
            columnData.push([j, i, renderData.seriesTotals[i]]);
        }
        // And grand total.
        i = renderData.seriesNames.length;
        j = renderData.groupNames.length;
        columnData.push([j, i, renderData.grandTotal]);

        // Use theme service to set a dark-theme compatible color.
        const themeService = this.context.pageContext!.getService<IVssThemeService>("IVssThemeService");

        // Renders with solid white background on light theme.
        // Note: SVG doesn't invert in high contrast, so we're on the hook for that in this case.
        var totalsBackground = isHighContrastMode() ? "#000000" : themeService.getThemeValue("background-color");

        resultSeries.push({
            name: Resources.PivotTableTotalText,
            data: columnData,
            color: totalsBackground
        });

        return resultSeries;
    }
}

VssComponent.register("pivotTable", PivotTable);

export class HistoricalChartBase extends ChartBase {

    constructor(props: ChartProps, context: IVssComponentContext) {
        super(props, context);
        this.useSeriesColoring = true;
    }

    protected isTrendChart(): boolean { return true; }
    public getGroupByLimit(): number { return 10000; }
    public getSeriesLimit(): number {
        let width = this.props.width || NaN;
        return this.chartLegendSizeService.getHorizontalLegendElements(width);
    }
}

VssComponent.register("historicalChartBase", HistoricalChartBase);

export class AreaChart extends HistoricalChartBase {
    public getChartType(): string { return Contracts.ChartTypesConstants.Area; }

}

VssComponent.register("areaChart", AreaChart);

export class StackAreaChart extends HistoricalChartBase {
    constructor(props: ChartProps, context: IVssComponentContext) {
        super(props, context);
        this.transpose = true;
    }

    public getChartType(): string { return Contracts.ChartTypesConstants.StackedArea; }
}

VssComponent.register("stackAreaChart", StackAreaChart);

export class LineChart extends StackAreaChart {
    public getChartType(): string { return Contracts.ChartTypesConstants.Line; }
}

VssComponent.register("lineChart", LineChart);

export class ChartTypeHelper {
    public static getChartTypeComponent(props: ChartProps, chartType: ChartTypes): JSX.Element | undefined {
        switch (chartType) {
            case ChartTypes.barChart: return <BarChart {...props} />;
            case ChartTypes.pieChart: return <PieChart {...props} />;
            case ChartTypes.columnChart: return <ColumnChart {...props} />;
            case ChartTypes.stackBarChart: return <StackedBarChart {...props} />;
            case ChartTypes.pivotTable: return <PivotTable {...props} />;
            case ChartTypes.stackAreaChart: return <StackAreaChart {...props} />;
            case ChartTypes.areaChart: return <AreaChart {...props} />;
            case ChartTypes.lineChart: return <LineChart {...props} />;
            default: return;
        }
    }
}